































































































































































































import Vue, { PropType } from 'vue';

import * as Utils from '@/utils';
import { PeerGroupMember } from '@/models/orgStructure';
import {
  WebRequestStatus,
  BaseUser,
  MemberRole,
  BasePeerGroup
} from '@/models';
import { GroupRoleFilter, groupRoleName, groupRolePlural } from '@/groupRole';

import UnrulrFilter from '@/components/Filter.vue';
import LazyRenderingList from '@/components/LazyRenderingList.vue';
import OrganizationUserCreate from '@/components/OrganizationUserCreate.vue';
import OrganizationUserCreateMultiple from '@/components/OrganizationUserCreateMultiple.vue';
import OrganizationPeerGroupMember from '@/components/OrganizationPeerGroupMember.vue';
import PeerGroupMemberActionsMenu from '@/components/PeerGroupMemberActionsMenu.vue';
import PeerGroupRoleInfo from '@/components/info/PeerGroupRoleInfo.vue';
import PeerGroupRoleFilterControl from '@/components/PeerGroupRoleFilterControl.vue';
import PeerGroupRoleSelectorPills from '@/components/PeerGroupRoleSelectorPills.vue';

type ListMember = [PeerGroupMember, WebRequestStatus, boolean];

export default Vue.extend({
  components: {
    UnrulrFilter,
    LazyRenderingList,
    OrganizationUserCreate,
    OrganizationUserCreateMultiple,
    OrganizationPeerGroupMember,
    PeerGroupMemberActionsMenu,
    PeerGroupRoleInfo,
    PeerGroupRoleFilterControl,
    PeerGroupRoleSelectorPills
  },

  props: {
    members: {
      type: Array as PropType<[PeerGroupMember, WebRequestStatus][]>,
      required: true
    },

    nonMembers: {
      type: Array as PropType<[PeerGroupMember, WebRequestStatus][]>,
      required: true
    },

    membersFirst: {
      type: Boolean,
      default: true
    },

    peerGroup: {
      type: Object as PropType<BasePeerGroup>,
      required: true
    },

    canDuplicate: Boolean
  },

  data() {
    return {
      rawFilter: '',
      inputFocused: false,
      multiRole: MemberRole.STUDENT,
      // Deliberately not saved, as on the org Members tab.
      roleFilter: 'ALL' as GroupRoleFilter
    };
  },

  computed: {
    filter(): string {
      return Utils.trimBrackets(this.rawFilter.trim());
    },

    showToolbar(): boolean {
      return this.members.length > 0;
    },

    // New people join in the filtered role, or as students under ALL.
    newMemberRole(): MemberRole {
      return this.roleFilter === 'ALL' ? MemberRole.STUDENT : this.roleFilter;
    },

    newMemberRoleName(): string {
      return groupRoleName(this.newMemberRole, this.peerGroup);
    },

    // Members matching the search, in every role.
    searchedMembers(): ListMember[] {
      return Utils.findTerm(this.filter, this.members, ([m]) => [
        m.name,
        m.emails.map(ue => ue.email).join(' ')
      ]).map(([m, status]) => [m, status, true]);
    },

    // By saved role: an unsaved pill change doesn't move the row until Save.
    filteredMembers(): ListMember[] {
      if (this.roleFilter === 'ALL') {
        return this.searchedMembers;
      }

      return this.searchedMembers.filter(([m]) => m.role === this.roleFilter);
    },

    // What Copy emails copies: the role filter applies, the search doesn't.
    roleMembers(): PeerGroupMember[] {
      const members = this.members.map(([m]) => m);

      if (this.roleFilter === 'ALL') {
        return members;
      }

      return members.filter(m => m.role === this.roleFilter);
    },

    copyRoleName(): string | undefined {
      if (this.roleFilter === 'ALL') {
        return undefined;
      }

      return groupRoleName(this.roleFilter, this.peerGroup);
    },

    hiddenByRole(): number {
      return this.searchedMembers.length - this.filteredMembers.length;
    },

    // Not yet in the group, so the role filter doesn't hide them; their role
    // pills start on the role they'd join in.
    newMemberOptions(): ListMember[] {
      return this.searchedNonMembers.map(([m, status]) => [
        { ...m, role: this.newMemberRole },
        status,
        false
      ]);
    },

    searchedNonMembers(): [PeerGroupMember, WebRequestStatus][] {
      if (!this.filter) {
        return this.membersFirst ? [] : this.nonMembers;
      }

      return Utils.findTerm(this.filter, this.nonMembers, ([m]) => [
        m.name,
        m.emails.map(ue => ue.email).join(' ')
      ]);
    },

    showRoleHint(): boolean {
      if (this.roleFilter === 'ALL' || this.showMultiUserCreate) {
        return false;
      }

      return this.filter
        ? this.hiddenByRole > 0
        : this.filteredMembers.length === 0;
    },

    roleHintText(): string {
      if (this.roleFilter === 'ALL') {
        return '';
      }

      if (!this.filter) {
        const plural = groupRolePlural(this.roleFilter, this.peerGroup);

        return `No ${plural} in this group.`;
      }

      const noun = this.hiddenByRole === 1 ? 'match' : 'matches';

      return `${this.hiddenByRole} ${noun} in other roles.`;
    },

    listed(): ListMember[] {
      if (this.membersFirst) {
        return [...this.filteredMembers, ...this.newMemberOptions];
      }

      return [...this.newMemberOptions, ...this.filteredMembers];
    },

    exactMatch(): boolean {
      if (!this.filter) {
        return false;
      }

      return false;
    },

    showUserCreate(): boolean {
      if (!this.filter) {
        return false;
      }

      if (this.showMultiUserCreate) {
        return false;
      }

      if (this.searchedMembers.length + this.newMemberOptions.length === 0) {
        return true;
      }

      // Members in every role count, so we never offer to create someone
      // the role filter is hiding.
      const noExactMatch = [...this.searchedMembers, ...this.newMemberOptions]
        .flatMap(([lm]) => lm.emails)
        .every(userEmail => userEmail.email !== this.filter);

      // Filter does not match any existing emails
      return noExactMatch;
    },

    showMultiUserCreate(): boolean {
      const tokenCount = Utils.parseByDelimiter(this.filter).length;

      return tokenCount > 1;
    },

    multiCreateBlockingUsers(): PeerGroupMember[] {
      return this.members.map(([m]) => m);
    },

    multiCreateAllowedUsers(): BaseUser[] {
      return this.nonMembers.map(([nm]) => ({ ...nm, role: undefined }));
    },

    placeholderText(): string {
      return 'Type to find or add a member';
    }
  },

  methods: {
    updateFilter(value: string) {
      this.rawFilter = value;
    },

    changeFocus(focused: boolean) {
      this.inputFocused = focused;
    },

    validateAndCreateUser(email: string) {
      if (!Utils.validateEmail(email)) {
        return;
      }

      this.$emit('create-member', { email, role: this.newMemberRole });
    },

    addMultiUser(newUser: string | BaseUser) {
      if (typeof newUser === 'string') {
        this.$emit('create-member', { email: newUser, role: this.multiRole });
      } else {
        this.$emit('add-member', { ...newUser, role: this.multiRole });
      }
    },

    addMultiUsers(values: (string | PeerGroupMember)[]) {
      const emails: string[] = [];
      const users: PeerGroupMember[] = [];

      for (const v of values) {
        if (typeof v === 'string') {
          emails.push(v);
        } else {
          users.push({ ...v, role: this.multiRole });
        }
      }

      this.$emit('add-members', users);
      this.$emit('create-members', { emails, role: this.multiRole });
    },

    changeRole(member: PeerGroupMember) {
      this.$emit('change-role', member);
    },

    addMember(member: PeerGroupMember) {
      this.$emit('add-member', member);
    },

    removeMember(member: PeerGroupMember) {
      this.$emit('remove-member', member);
    },

    clearFilter() {
      this.rawFilter = '';
    },

    handleRoleChange(role: MemberRole) {
      this.multiRole = role;
    },

    selectRoleFilter(roleFilter: GroupRoleFilter) {
      this.roleFilter = roleFilter;
    }
  },

  watch: {
    // The pasted-list panel starts on the filtered role, but can still be
    // changed there.
    newMemberRole(role: MemberRole) {
      this.multiRole = role;
    }
  }
});
